import React from "react";
import { Link, Navigate, Outlet, useLocation } from "react-router-dom";
import { toast } from "sonner";
import {
  LayoutDashboard,
  Calendar,
  Mail,
  BarChart3,
  Scale,
  ScrollText,
  Gavel,
  BookOpen,
  FileSearch,
  GraduationCap,
  ShieldCheck,
  Wrench,
  History,
  Instagram,
  FileText,
} from "lucide-react";
import { useI18n } from "../lib/i18n";
import { useAuth } from "../context/AuthContext";
import { api } from "../lib/api";

const NAV = [
  { to: "/admin", end: true, icon: LayoutDashboard, ar: "النظرة العامة", en: "Overview" },
  { to: "/admin/bookings", icon: Calendar, ar: "الحجوزات", en: "Bookings" },
  { to: "/admin/contacts", icon: Mail, ar: "الاستفسارات", en: "Inquiries" },
  { to: "/admin/analytics", icon: BarChart3, ar: "التحليلات والمستخدمون", en: "Analytics & Users" },
  { to: "/admin/laws", icon: Scale, ar: "مكتبة القوانين", en: "Laws Library" },
  { to: "/admin/law-queue", icon: ScrollText, ar: "طابور مراجعة القوانين", en: "Law Review Queue" },
  { to: "/admin/tools", icon: Wrench, ar: "أدوات المكتبة", en: "Library Tools" },
  { to: "/admin/audit-log", icon: History, ar: "سجل العمليات", en: "Audit Log" },
  { to: "/admin/judgments", icon: Gavel, ar: "الأحكام", en: "Judgments" },
  { to: "/admin/principles", icon: BookOpen, ar: "مبادئ التمييز", en: "Cassation Principles" },
  { to: "/admin/research", icon: FileSearch, ar: "الأبحاث", en: "Research Vault" },
  { to: "/admin/training", icon: GraduationCap, ar: "تدريب سامي", en: "Sami Training" },
  { to: "/admin/staff", icon: ShieldCheck, ar: "الفريق والصلاحيات", en: "Team & Roles" },
  { to: "/admin/social-topics", icon: Instagram, ar: "مواضيع السوشيال", en: "Social Topics" },
  { to: "/admin/blog-drafts", icon: FileText, ar: "مسوّدات المدوّنة", en: "Blog Drafts" },
];

export default function AdminLayout() {
  const { lang } = useI18n();
  const { user, loading } = useAuth();
  const location = useLocation();
  const isAr = lang === "ar";
  const [queueCount, setQueueCount] = React.useState(0);

  // Poll ingestion/queue status (admin only) for the live queue badge + a
  // one-time "OCR finished, laws ready for approval" notification.
  React.useEffect(() => {
    if (!user || user.role !== "admin") return;
    let alive = true;
    const poll = async () => {
      try {
        const r = await api.get("/admin/ingest-status");
        if (!alive) return;
        const d = r.data || {};
        setQueueCount(d.queue_pending || 0);
        if (d.complete && d.queue_pending > 0 && d.bundle_updated_at) {
          const seen = localStorage.getItem("ocr_done_notified");
          if (seen !== d.bundle_updated_at) {
            localStorage.setItem("ocr_done_notified", d.bundle_updated_at);
            toast.success(
              isAr
                ? `اكتملت معالجة القوانين — ${d.queue_pending} بانتظار الاعتماد`
                : `OCR finished — ${d.queue_pending} laws ready for approval`,
              {
                description: isAr
                  ? "افتح طابور مراجعة القوانين لاعتمادها."
                  : "Open the Law Review Queue to approve them.",
                duration: 12000,
              }
            );
          }
        }
      } catch {
        /* ignore poll errors */
      }
    };
    poll();
    const t = setInterval(poll, 25000);
    return () => {
      alive = false;
      clearInterval(t);
    };
  }, [user, isAr]);

  if (loading) {
    return <div className="p-20 text-center text-slate-500 text-sm">Loading…</div>;
  }
  if (!user) return <Navigate to="/login?next=/admin" replace />;
  if (user.role !== "admin") return <Navigate to="/" replace />;

  const isActive = (item) =>
    item.end
      ? location.pathname === item.to || location.pathname === item.to + "/"
      : location.pathname === item.to || location.pathname.startsWith(item.to + "/");

  return (
    <div className="max-w-[1600px] mx-auto px-4 lg:px-6 py-6 lg:py-8" data-testid="admin-layout">
      <div className="flex flex-col lg:flex-row gap-6">
        {/* Sidebar (right in RTL) */}
        <aside
          data-testid="admin-sidebar"
          className="lg:w-64 shrink-0"
        >
          <div className="lg:sticky lg:top-24">
            <div className="hidden lg:block mb-4 px-3">
              <p className="eyebrow">{isAr ? "لوحة التحكم" : "Admin"}</p>
              <p className="font-display text-xl mt-1 leading-tight text-slate-900 dark:text-slate-100">
                {isAr ? "مجموعة الدخيل" : "Aldakheel"}
              </p>
            </div>
            <nav
              className="flex lg:flex-col gap-1 overflow-x-auto lg:overflow-visible
                         border border-slate-200 dark:border-slate-800
                         bg-white dark:bg-slate-900 rounded-sm p-2"
            >
              {NAV.map((item) => {
                const active = isActive(item);
                const Icon = item.icon;
                return (
                  <Link
                    key={item.to}
                    to={item.to}
                    data-testid={`admin-nav-${item.to.replace(/\//g, "-").replace(/^-/, "")}`}
                    className={`group flex items-center gap-3 shrink-0 whitespace-nowrap
                      px-3 py-2.5 rounded-sm text-sm transition-colors
                      ${active
                        ? "bg-slate-900 text-white dark:bg-amber-500/90 dark:text-slate-900"
                        : "text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800"}`}
                  >
                    <Icon className="w-4 h-4 shrink-0" />
                    <span>{isAr ? item.ar : item.en}</span>
                    {item.to === "/admin/law-queue" && queueCount > 0 && (
                      <span
                        data-testid="admin-queue-badge"
                        className={`ms-auto shrink-0 min-w-[20px] text-center text-[11px]
                          font-semibold px-1.5 py-0.5 rounded-full
                          ${active ? "bg-white/25 text-white" : "bg-rose-600 text-white"}`}
                      >
                        {queueCount > 99 ? "99+" : queueCount}
                      </span>
                    )}
                  </Link>
                );
              })}
            </nav>
          </div>
        </aside>

        {/* Content */}
        <main className="flex-1 min-w-0" data-testid="admin-content">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
